import React, { useState } from 'react';
import { Users, Calendar, Bus, FileText, Home, School, Clock, AlertCircle, Plus, Edit2, Trash2, Save, X, Check, MapPin, Phone, Mail } from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';

// DEMO DATA
const DEMO_DATA = {
  currentAdmin: {
    id: 'admin_001',
    firstName: 'David',
    lastName: 'Anderson',
    email: 'david.anderson@brischool.edu',
    role: 'Vice Principal'
  },

  substituteRequests: [
    {
      id: 'sub_001',
      teacherId: 'tchr_002',
      teacherName: 'James Chen',
      subject: 'Mathematics',
      date: '2025-01-10',
      period: '2nd Period (10:15-11:15)',
      room: '301',
      reason: 'Medical Appointment',
      status: 'pending',
      submittedAt: '2025-01-05T09:00:00'
    },
    {
      id: 'sub_002',
      teacherId: 'tchr_003',
      teacherName: 'Aisha Patel',
      subject: 'Science',
      date: '2025-01-12',
      period: '1st Period (9:00-10:30)',
      room: '105',
      reason: 'Professional Development',
      status: 'approved',
      assignedTo: 'Robert Martinez',
      submittedAt: '2025-01-04T14:30:00',
      approvedAt: '2025-01-05T10:00:00'
    }
  ],

  availableSubstitutes: [
    { id: 'sub_t001', name: 'Robert Martinez', subjects: ['Science', 'Mathematics'], phone: '555-0201', email: 'r.martinez@brischool.edu', availability: 'Available' },
    { id: 'sub_t002', name: 'Emily Thompson', subjects: ['English', 'History'], phone: '555-0202', email: 'e.thompson@brischool.edu', availability: 'Available' },
    { id: 'sub_t003', name: 'Michael Foster', subjects: ['Physical Education'], phone: '555-0203', email: 'm.foster@brischool.edu', availability: 'Unavailable' }
  ],

  busRoutes: [
    {
      id: 'route_001',
      routeNumber: 'Route 1',
      driver: 'John Smith',
      capacity: 45,
      currentRiders: 38,
      status: 'active',
      schedule: '7:30 AM - 8:15 AM',
      stops: [
        { id: 'stop_001', location: 'Oak Street & Main', time: '7:30 AM', students: 8 },
        { id: 'stop_002', location: 'Elm Avenue & Park', time: '7:45 AM', students: 12 },
        { id: 'stop_003', location: 'Pine Road & School', time: '8:00 AM', students: 18 }
      ]
    },
    {
      id: 'route_002',
      routeNumber: 'Route 2',
      driver: 'Maria Garcia',
      capacity: 45,
      currentRiders: 42,
      status: 'active',
      schedule: '7:15 AM - 8:00 AM',
      stops: [
        { id: 'stop_004', location: 'Maple Drive & Center', time: '7:15 AM', students: 15 },
        { id: 'stop_005', location: 'Cedar Lane & North', time: '7:35 AM', students: 14 },
        { id: 'stop_006', location: 'Birch Street & School', time: '7:55 AM', students: 13 }
      ]
    }
  ],

  staffDirectory: [
    { id: 'staff_001', name: 'Sarah Mitchell', role: 'English Teacher', department: 'English', email: 'sarah.mitchell@brischool.edu', phone: '555-0101', office: 'Room 204' },
    { id: 'staff_002', name: 'James Chen', role: 'Mathematics Teacher', department: 'Mathematics', email: 'james.chen@brischool.edu', phone: '555-0102', office: 'Room 301' },
    { id: 'staff_003', name: 'Aisha Patel', role: 'Science Teacher', department: 'Science', email: 'aisha.patel@brischool.edu', phone: '555-0103', office: 'Room 105' },
    { id: 'staff_004', name: 'David Anderson', role: 'Vice Principal', department: 'Administration', email: 'david.anderson@brischool.edu', phone: '555-0104', office: 'Main Office' }
  ]
};

const BriSchoolAdminBootstrap = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [substituteRequests, setSubstituteRequests] = useState(DEMO_DATA.substituteRequests);
  const [assigningSubstitute, setAssigningSubstitute] = useState(null);
  const [selectedSubstitute, setSelectedSubstitute] = useState('');

  const approveSubstitute = (requestId) => {
    if (!selectedSubstitute) {
      alert('Please select a substitute teacher');
      return;
    }

    const substitute = DEMO_DATA.availableSubstitutes.find(s => s.id === selectedSubstitute);
    
    setSubstituteRequests(substituteRequests.map(req =>
      req.id === requestId
        ? {
            ...req,
            status: 'approved',
            assignedTo: substitute.name,
            approvedAt: new Date().toISOString()
          }
        : req
    ));
    
    setAssigningSubstitute(null);
    setSelectedSubstitute('');
    alert(`Substitute assigned: ${substitute.name}`);
  };

  const denySubstitute = (requestId) => {
    setSubstituteRequests(substituteRequests.map(req =>
      req.id === requestId
        ? {
            ...req,
            status: 'denied',
            deniedAt: new Date().toISOString()
          }
        : req
    ));
    alert('Substitute request denied');
  };

  const getStatusBadge = (status) => {
    if (status === 'pending') return 'badge bg-warning';
    if (status === 'approved') return 'badge bg-success';
    if (status === 'denied') return 'badge bg-danger';
    return 'badge bg-secondary';
  };

  // Assigning Substitute View
  if (assigningSubstitute) {
    const request = assigningSubstitute;

    return (
      <div className="min-vh-100 bg-light">
        <nav className="navbar navbar-light bg-white border-bottom shadow-sm">
          <div className="container-fluid">
            <button onClick={() => setAssigningSubstitute(null)} className="btn btn-link text-decoration-none">
              ← Back
            </button>
            <span className="navbar-brand mb-0 h1">Assign Substitute Teacher</span>
          </div>
        </nav>

        <div className="container py-4">
          <div className="row">
            <div className="col-lg-4">
              <div className="card shadow mb-4">
                <div className="card-body">
                  <h5 className="card-title">Request Details</h5>
                  <div className="mb-2">
                    <small className="text-muted">Teacher:</small>
                    <p className="fw-bold mb-0">{request.teacherName}</p>
                  </div>
                  <div className="mb-2">
                    <small className="text-muted">Subject:</small>
                    <p className="fw-bold mb-0">{request.subject}</p>
                  </div>
                  <div className="mb-2">
                    <small className="text-muted">Date:</small>
                    <p className="fw-bold mb-0">{request.date}</p>
                  </div>
                  <div className="mb-2">
                    <small className="text-muted">Period:</small>
                    <p className="fw-bold mb-0">{request.period}</p>
                  </div>
                  <div className="mb-2">
                    <small className="text-muted">Room:</small>
                    <p className="fw-bold mb-0">{request.room}</p>
                  </div>
                  <div>
                    <small className="text-muted">Reason:</small>
                    <p className="mb-0">{request.reason}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-8">
              <h5 className="mb-3">Select Substitute Teacher</h5>
              <div className="row g-3">
                {DEMO_DATA.availableSubstitutes.map(sub => {
                  const canTeachSubject = sub.subjects.includes(request.subject);
                  const isAvailable = sub.availability === 'Available';
                  const isSelectable = canTeachSubject && isAvailable;

                  return (
                    <div key={sub.id} className="col-md-6">
                      <div
                        className={`card h-100 ${
                          selectedSubstitute === sub.id ? 'border-primary border-2' : ''
                        } ${!isSelectable ? 'opacity-50' : ''}`}
                        role={isSelectable ? 'button' : undefined}
                        onClick={() => isSelectable && setSelectedSubstitute(sub.id)}
                        style={{cursor: isSelectable ? 'pointer' : 'not-allowed'}}
                      >
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="card-title mb-0">{sub.name}</h6>
                            {selectedSubstitute === sub.id && (
                              <Check className="text-primary" size={24} />
                            )}
                          </div>
                          <p className="text-muted small mb-2">
                            Subjects: {sub.subjects.join(', ')}
                          </p>
                          <div className="small mb-1">
                            <Phone size={14} className="me-1" />
                            {sub.phone}
                          </div>
                          <div className="small mb-2">
                            <Mail size={14} className="me-1" />
                            {sub.email}
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <span className={`badge ${isAvailable ? 'bg-success' : 'bg-danger'}`}>
                              {sub.availability}
                            </span>
                            {!canTeachSubject && (
                              <small className="text-danger">Cannot teach {request.subject}</small>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 d-grid gap-2 d-md-flex justify-content-md-end">
                <button
                  onClick={() => approveSubstitute(request.id)}
                  disabled={!selectedSubstitute}
                  className="btn btn-success btn-lg"
                >
                  <Check size={20} className="me-2" />
                  Approve & Assign
                </button>
                <button
                  onClick={() => denySubstitute(request.id)}
                  className="btn btn-danger btn-lg"
                >
                  <X size={20} className="me-2" />
                  Deny Request
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 bg-light">
      {/* Header */}
      <nav className="navbar navbar-light bg-white border-bottom shadow-sm">
        <div className="container-fluid">
          <span className="navbar-brand mb-0 h1">
            <School className="me-2" size={32} style={{color: '#6c757d'}} />
            BriSchool Admin Portal
          </span>
          <div className="d-flex align-items-center">
            <div className="text-end me-3">
              <div className="fw-bold">{DEMO_DATA.currentAdmin.firstName} {DEMO_DATA.currentAdmin.lastName}</div>
              <small className="text-muted">{DEMO_DATA.currentAdmin.role}</small>
            </div>
            <div className="bg-secondary text-white rounded-circle d-flex align-items-center justify-content-center" 
                 style={{width: '40px', height: '40px', fontWeight: 'bold'}}>
              {DEMO_DATA.currentAdmin.firstName[0]}{DEMO_DATA.currentAdmin.lastName[0]}
            </div>
          </div>
        </div>
      </nav>

      {/* Navigation */}
      <div className="bg-white border-bottom">
        <div className="container-fluid">
          <ul className="nav nav-pills py-2">
            <li className="nav-item">
              <button onClick={() => setActiveTab('dashboard')} className={`nav-link ${activeTab === 'dashboard' ? 'active' : ''}`}>
                <Home size={16} className="me-2" />Dashboard
              </button>
            </li>
            <li className="nav-item">
              <button onClick={() => setActiveTab('substitutes')} className={`nav-link ${activeTab === 'substitutes' ? 'active' : ''}`}>
                <Users size={16} className="me-2" />Substitutes
              </button>
            </li>
            <li className="nav-item">
              <button onClick={() => setActiveTab('buses')} className={`nav-link ${activeTab === 'buses' ? 'active' : ''}`}>
                <Bus size={16} className="me-2" />Bus Routes
              </button>
            </li>
            <li className="nav-item">
              <button onClick={() => setActiveTab('staff')} className={`nav-link ${activeTab === 'staff' ? 'active' : ''}`}>
                <FileText size={16} className="me-2" />Staff Directory
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-fluid py-4">
        {activeTab === 'dashboard' && (
          <div>
            <h2 className="mb-4">Admin Dashboard</h2>

            <div className="row g-4 mb-4">
              <div className="col-md-4">
                <div className="card text-white bg-warning shadow" role="button" onClick={() => setActiveTab('substitutes')}>
                  <div className="card-body">
                    <Users size={32} className="mb-2" />
                    <h6 className="opacity-75">Pending Substitute Requests</h6>
                    <h2>{substituteRequests.filter(r => r.status === 'pending').length}</h2>
                    <small>View requests →</small>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="card text-white bg-info shadow" role="button" onClick={() => setActiveTab('buses')}>
                  <div className="card-body">
                    <Bus size={32} className="mb-2" />
                    <h6 className="opacity-75">Active Bus Routes</h6>
                    <h2>{DEMO_DATA.busRoutes.filter(r => r.status === 'active').length}</h2>
                    <small>Manage routes →</small>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="card text-white bg-secondary shadow" role="button" onClick={() => setActiveTab('staff')}>
                  <div className="card-body">
                    <FileText size={32} className="mb-2" />
                    <h6 className="opacity-75">Staff Members</h6>
                    <h2>{DEMO_DATA.staffDirectory.length}</h2>
                    <small>View directory →</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-4">
              <div className="col-lg-6">
                <div className="card shadow">
                  <div className="card-header">
                    <h5 className="mb-0">Recent Substitute Requests</h5>
                  </div>
                  <div className="list-group list-group-flush">
                    {substituteRequests.slice(0, 3).map(request => (
                      <div key={request.id} className="list-group-item">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="mb-1">{request.teacherName}</h6>
                            <p className="text-muted mb-0 small">{request.subject} • {request.date}</p>
                          </div>
                          <span className={getStatusBadge(request.status)}>
                            {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="card shadow">
                  <div className="card-header">
                    <h5 className="mb-0">Bus Route Status</h5>
                  </div>
                  <div className="list-group list-group-flush">
                    {DEMO_DATA.busRoutes.map(route => (
                      <div key={route.id} className="list-group-item">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="mb-1">{route.routeNumber}</h6>
                            <p className="text-muted mb-0 small">Driver: {route.driver}</p>
                            <p className="text-muted mb-0 small">Riders: {route.currentRiders}/{route.capacity}</p>
                          </div>
                          <span className="badge bg-success">Active</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'substitutes' && (
          <div>
            <h2 className="mb-4">Substitute Teacher Requests</h2>

            <div className="card shadow mb-4">
              <div className="card-header">
                <h5 className="mb-0">Pending Requests</h5>
              </div>
              <div className="list-group list-group-flush">
                {substituteRequests.filter(r => r.status === 'pending').length > 0 ? (
                  substituteRequests.filter(r => r.status === 'pending').map(request => (
                    <div key={request.id} className="list-group-item">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">{request.teacherName} - {request.subject}</h6>
                          <p className="text-muted mb-1 small">
                            Date: {request.date} • Period: {request.period} • Room: {request.room}
                          </p>
                          <p className="text-muted mb-0 small">Reason: {request.reason}</p>
                        </div>
                        <button
                          onClick={() => setAssigningSubstitute(request)}
                          className="btn btn-primary"
                        >
                          Assign Substitute
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="list-group-item text-center py-4">
                    <p className="text-muted mb-0">No pending substitute requests</p>
                  </div>
                )}
              </div>
            </div>

            <div className="card shadow">
              <div className="card-header">
                <h5 className="mb-0">Approved Requests</h5>
              </div>
              <div className="list-group list-group-flush">
                {substituteRequests.filter(r => r.status === 'approved').map(request => (
                  <div key={request.id} className="list-group-item">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-1">{request.teacherName} - {request.subject}</h6>
                        <p className="text-muted mb-1 small">Date: {request.date} • Period: {request.period}</p>
                        <p className="mb-0 small">
                          <span className="badge bg-success">Assigned to: {request.assignedTo}</span>
                        </p>
                      </div>
                      <span className={getStatusBadge(request.status)}>Approved</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'buses' && (
          <div>
            <h2 className="mb-4">Bus Route Management</h2>

            {DEMO_DATA.busRoutes.map(route => (
              <div key={route.id} className="card shadow mb-4">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <h4 className="card-title mb-1">{route.routeNumber}</h4>
                      <p className="text-muted mb-0">Driver: {route.driver}</p>
                      <p className="text-muted mb-0">Schedule: {route.schedule}</p>
                    </div>
                    <div className="text-end">
                      <span className="badge bg-success mb-2">{route.status.toUpperCase()}</span>
                      <div className="card bg-light">
                        <div className="card-body p-2 text-center">
                          <small className="text-muted d-block">Capacity</small>
                          <h5 className={`mb-0 ${route.currentRiders > route.capacity * 0.9 ? 'text-warning' : 'text-success'}`}>
                            {route.currentRiders}/{route.capacity}
                          </h5>
                        </div>
                      </div>
                    </div>
                  </div>

                  <h6 className="mb-3">Route Stops</h6>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead className="table-light">
                        <tr>
                          <th>Stop</th>
                          <th>Location</th>
                          <th className="text-center">Time</th>
                          <th className="text-center">Students</th>
                        </tr>
                      </thead>
                      <tbody>
                        {route.stops.map((stop, idx) => (
                          <tr key={stop.id}>
                            <td className="fw-bold">Stop {idx + 1}</td>
                            <td>
                              <MapPin size={14} className="me-1" />
                              {stop.location}
                            </td>
                            <td className="text-center">
                              <Clock size={14} className="me-1" />
                              {stop.time}
                            </td>
                            <td className="text-center">
                              <span className="badge bg-info">{stop.students}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'staff' && (
          <div>
            <h2 className="mb-4">Staff Directory</h2>

            <div className="card shadow">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Department</th>
                      <th>Contact</th>
                      <th>Office</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DEMO_DATA.staffDirectory.map(staff => (
                      <tr key={staff.id}>
                        <td className="fw-bold">{staff.name}</td>
                        <td>{staff.role}</td>
                        <td>{staff.department}</td>
                        <td>
                          <div className="small">
                            <Phone size={12} className="me-1" />
                            {staff.phone}
                          </div>
                          <div className="small">
                            <Mail size={12} className="me-1" />
                            {staff.email}
                          </div>
                        </td>
                        <td>{staff.office}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BriSchoolAdminBootstrap;